import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import { toggle } from "@/state/toggle/toggle.slice";
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Menu,
  LayoutDashboard,
  Package,
  ShoppingCart, DollarSign,
  Users,
  Truck,
  Box,
  Settings,
  BarChart3,
  Tag,
  Shield,
  Store,
  KeyRound,
  Ruler,
  Activity,
  Receipt,
  ListOrdered,
  PlusCircle,
  Building2,
  Undo2,
  ChartLine,
} from "lucide-react";
// Using standard img tags instead of next/image

// Navigation items organized by groups
const navGroups = [
  {
    name: "Overview",
    items: [
      {
        name: "Dashboard",
        icon: LayoutDashboard,
        href: "/dashboard",
        permission: "Dashboard"
      },
    ]
  },
  {
    name: "Sales & Orders",
    items: [
      {
        name: "Sales",
        icon: DollarSign,
        href: "/dashboard/sales",
        permission: "Sales",
        subItems: [
          { name: "All Sales", icon: ListOrdered, href: "/dashboard/sales" },
          { name: "New Sale", icon: PlusCircle, href: "/dashboard/sales/create" },
          { name: "Drafts", icon: Receipt, href: "/dashboard/sales/drafts" },
        ],
      },
      {
        name: "Customers",
        icon: Users,
        href: "/dashboard/customers",
        permission: "Sales"
      },
    ]
  },
  {
    name: "Inventory",
    items: [
      {
        name: "Products",
        icon: Box,
        href: "/dashboard/products",
        permission: "Products",
        subItems: [
          { name: "Products List", icon: ListOrdered, href: "/dashboard/products" },
          { name: "Add Product", icon: PlusCircle, href: "/dashboard/products/add-product" },
        ],
      },
      {
        name: "Stock Overview",
        icon: Package,
        href: "/dashboard/inventory",
        permission: "Products"
      },
      {
        name: "Purchases",
        icon: ShoppingCart,
        href: "/dashboard/purchases",
        permission: "Purchases",
        subItems: [
          { name: "All Purchases", icon: ListOrdered, href: "/dashboard/purchases" },
          { name: "Add Purchase", icon: PlusCircle, href: "/dashboard/purchases/add" },
          { name: "Purchase Returns", icon: Undo2, href: "/dashboard/purchases/returns-all/returns" },
        ],
      },
      {
        name: "Suppliers",
        icon: Truck,
        href: "/dashboard/suppliers",
        permission: "Sales"
      },
    ]
  },
  {
    name: "Reports & Analytics",
    items: [
      {
        name: "Reports",
        icon: BarChart3,
        href: "/dashboard/reports",
        permission: "Settings",
        subItems: [
          { name: "Sales Report", icon: ChartLine, href: "/dashboard/reports/sales" },
          { name: "Activity Logs", icon: Activity, href: "/dashboard/reports/activities" },
          { name: "Expenses", icon: Receipt, href: "/dashboard/reports/expenses" },
        ],
      },
    ]
  },
  {
    name: "Configuration",
    items: [
      {
        name: "Settings",
        icon: Settings,
        href: "/dashboard/settings",
        permission: "Settings",
        subItems: [
          { name: "Business", icon: Building2, href: "/dashboard/settings/business" },
          { name: "Shops", icon: Store, href: "/dashboard/settings/shops" },
          { name: "Categories", icon: Tag, href: "/dashboard/settings/categories" },
          { name: "Brands", icon: Tag, href: "/dashboard/settings/brands" },
          { name: "Units", icon: Ruler, href: "/dashboard/settings/units" },
          { name: "Warranties", icon: Shield, href: "/dashboard/settings/warranties" },
          { name: "Discounts", icon: Receipt, href: "/dashboard/settings/discounts" },
        ],
      },
      {
        name: "Users & Roles",
        icon: Shield,
        href: "/dashboard/settings/users",
        permission: "Settings",
        subItems: [
          { name: "Users", icon: Users, href: "/dashboard/settings/users" },
          { name: "Roles", icon: KeyRound, href: "/dashboard/settings/roles" },
        ],
      },
    ]
  },
];

export default function SleekSidebar() {
  const [isMobile, setIsMobile] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [isMounted, setIsMounted] = useState(false);
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);

  const { user } = useSelector((state) => state.auth);
  const { isOpen } = useSelector((state) => state.toggle);
  const dispatch = useDispatch();

  useEffect(() => {
    setIsMounted(true);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        dispatch(toggle());
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isOpen, dispatch]);

  const toggleSubmenu = (name) => {
    setOpenSubmenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const isActive = (href) => pathname === href;
  const isParentActive = (item) => {
    if (isActive(item.href)) return true;
    if (item.subItems) {
      return item.subItems.some((sub) => pathname === sub.href);
    }
    return false;
  };

  // Filter navigation groups and items based on permissions
  const getFilteredGroups = () => {
    if (!user || !user.role || !user.role.permissions) return [];

    let userPerms = [];
    try {
      userPerms = typeof user.role.permissions === 'string'
        ? JSON.parse(user.role.permissions)
        : user.role.permissions;
    } catch (e) {
      return [];
    }

    return navGroups.map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (!item.permission) return true;
        return userPerms.includes(item.permission);
      })
    })).filter(group => group.items.length > 0);
  };

  const filteredNavGroups = getFilteredGroups();

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="outline"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => dispatch(toggle())}
      >
        <Menu className="h-4 w-4" />
      </Button>

      <AnimatePresence mode="wait" initial={false}>
        {(isOpen || !isMobile) && (
          <motion.div
            ref={sidebarRef}
            initial={{ width: isMobile ? 0 : 64, opacity: isMobile ? 0 : 1 }}
            animate={{ width: isOpen ? 240 : 64, opacity: 1 }}
            exit={{ width: isMobile ? 0 : 64, opacity: isMobile ? 0 : 1 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={cn(
              "fixed inset-y-0 left-0 z-40 flex flex-col",
              "bg-card text-card-foreground",
              "border-r border-border",
              isMobile && "shadow-2xl"
            )}
          >
            {/* Logo Header */}
            <div className="flex items-center h-16 px-4 border-b border-border">
              {isOpen ? (
                <div className="flex items-center gap-2">
                  <img
                    src="/assets/brand/logo-black.png"
                    className="w-auto h-12 md:h-16"
                    alt="CetaPOS Logo"
                  />
                </div>
              ) : (
                <div className="mx-auto">
                  <div className="h-8 w-8 bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <img
                      src="/assets/brand/icon.png"
                      className="w-auto h-6 md:h-10"
                      alt="CetaPOS Logo"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
              {isMounted && filteredNavGroups.map((group, groupIndex) => (
                <div key={group.name} className={cn(groupIndex > 0 && "mt-6")}>
                  {/* Group Header - only show when sidebar is open */}
                  {isOpen && (
                    <div className="px-4 mb-2">
                      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                        {group.name}
                      </h3>
                    </div>
                  )}
                  {/* Group separator for collapsed state */}
                  {!isOpen && groupIndex > 0 && (
                    <div className="mx-3 mb-2 border-t border-border" />
                  )}

                  {/* Group Items */}
                  <div className="space-y-0.5 px-2">
                    {group.items.map((item) => (
                      <div key={item.name}>
                        {item.subItems ? (
                          <div>
                            <button
                              onClick={() => {
                                if (!isOpen && !isMobile) {
                                  dispatch(toggle());
                                  setTimeout(() => toggleSubmenu(item.name), 100);
                                } else {
                                  toggleSubmenu(item.name);
                                }
                              }}
                              className={cn(
                                "w-full flex items-center gap-3 px-3 py-2 text-sm font-medium transition-all duration-150 rounded-none border border-transparent",
                                isParentActive(item)
                                  ? "bg-primary/5 text-primary border-primary/10"
                                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
                                !isOpen && "justify-center px-2"
                              )}
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0" />
                              {isOpen && (
                                <>
                                  <span className="flex-1 text-left truncate">{item.name}</span>
                                  {openSubmenus[item.name] ? (
                                    <ChevronUp className="h-3 w-3 opacity-50" />
                                  ) : (
                                    <ChevronDown className="h-3 w-3 opacity-50" />
                                  )}
                                </>
                              )}
                            </button>
                            <AnimatePresence>
                              {isOpen && openSubmenus[item.name] && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                  className="overflow-hidden"
                                >
                                  <div className="ml-7 mt-1 space-y-0.5 border-l border-border pl-3">
                                    {item.subItems.map((subItem) => (
                                      <Link
                                        key={subItem.name}
                                        to={subItem.href}
                                        onClick={() => isMobile && dispatch(toggle())}
                                      >
                                        <div
                                          className={cn(
                                            "flex items-center gap-2 px-2 py-1.5 text-xs transition-colors rounded-none border border-transparent",
                                            isActive(subItem.href)
                                              ? "text-primary bg-primary/10 border-primary/20"
                                              : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                          )}
                                        >
                                          <subItem.icon className="h-3 w-3" />
                                          <span className="truncate">{subItem.name}</span>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ) : (
                          <Link
                            to={item.href}
                            onClick={() => isMobile && dispatch(toggle())}
                          >
                            <div
                              className={cn(
                                "flex items-center gap-3 px-3 py-2 text-sm font-medium transition-all duration-150 rounded-none border border-transparent",
                                isActive(item.href)
                                  ? "bg-primary/5 text-primary border-primary/10"
                                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
                                !isOpen && "justify-center px-2"
                              )}
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0" />
                              {isOpen && <span className="truncate">{item.name}</span>}
                            </div>
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {/* Toggle Button */}
            {!isMobile && (
              <div className="p-2 border-t border-border">
                <button
                  onClick={() => dispatch(toggle())}
                  className="w-full flex items-center justify-center py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            )}

            {/* User Footer */}
            <div className="p-3 border-t border-border">
              {isOpen ? (
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 bg-muted flex items-center justify-center text-[10px] font-bold text-foreground border border-border">
                    {user?.username?.substring(0, 2)?.toUpperCase() || 'US'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{user?.username || 'User'}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{user?.role?.name || 'Role'}</p>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <div className="h-7 w-7 bg-muted flex items-center justify-center text-[10px] font-bold text-foreground border border-border">
                    {user?.username?.substring(0, 2)?.toUpperCase() || 'US'}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
